import { Patient, Doctor, Appointment, Contact, Opportunity, Service, Insurance, Specialty, ClinicalHistory, AuditLog } from '../types';
import { formatDate, getGenderName, getStatusName } from './helpers';
interface PdfColumn {
  header: string;
  key: string;
  width?: number;
}
interface PdfRow {
  [key: string]: string | number | boolean;
}
const downloadFormattedPdf = async (data: PdfRow[], columns: PdfColumn[], filename: string, title: string) => {
  if (data.length === 0) {
    alert('No hay datos para exportar');
    return;
  }

  // Dynamic import to avoid bundling jsPDF upfront
  const {
    default: jsPDF
  } = await import('jspdf');
  const doc = new jsPDF('l', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const colors = {
    primary: [20, 184, 166] as [number, number, number],
    dark: [17, 24, 39] as [number, number, number],
    gray: [107, 114, 128] as [number, number, number],
    lightGray: [229, 231, 235] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    headerBg: [15, 118, 110] as [number, number, number],
    rowEven: [240, 253, 250] as [number, number, number],
    accent: [6, 182, 212] as [number, number, number]
  };

  // Calculate column widths
  const totalDefinedWidth = columns.reduce((sum, c) => sum + (c.width || 0), 0);
  const colsWithoutWidth = columns.filter(c => !c.width).length;
  const defaultWidth = colsWithoutWidth > 0 ? (contentWidth - (totalDefinedWidth > 0 ? totalDefinedWidth / 200 * contentWidth : 0)) / colsWithoutWidth : contentWidth / columns.length;
  const colWidths = columns.map(c => {
    if (c.width) return c.width / totalDefinedWidth * contentWidth * 0.9;
    return defaultWidth;
  });
  const totalColWidth = colWidths.reduce((s, w) => s + w, 0);
  const scale = contentWidth / totalColWidth;
  const finalWidths = colWidths.map(w => w * scale);
  let y = margin;
  let pageNum = 1;
  const drawHeader = () => {
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 3, 'F');
    y = 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...colors.primary);
    doc.text(title, margin, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...colors.gray);
    doc.text(`Savidna - Centro de Bienestar y Belleza  |  ${data.length} registros  |  ${new Date().toLocaleDateString('es-DO')}`, margin, y + 13);
    y += 18;
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 4;
  };
  const drawTableHeader = () => {
    const rowHeight = 8;
    doc.setFillColor(...colors.headerBg);
    doc.rect(margin, y, contentWidth, rowHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...colors.white);
    let x = margin + 2;
    columns.forEach((col, i) => {
      const text = doc.splitTextToSize(col.header, finalWidths[i] - 4);
      doc.text(text[0] || '', x, y + 5.5);
      x += finalWidths[i];
    });
    y += rowHeight + 1;
  };
  const drawFooter = () => {
    doc.setFillColor(...colors.primary);
    doc.rect(0, pageHeight - 3, pageWidth, 3, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...colors.gray);
    doc.text(`Página ${pageNum}`, pageWidth / 2, pageHeight - 5, {
      align: 'center'
    });
    doc.text('Savidna - Documento Confidencial', margin, pageHeight - 5);
    doc.text(new Date().toLocaleDateString('es-DO'), pageWidth - margin, pageHeight - 5, {
      align: 'right'
    });
  };
  drawHeader();
  drawTableHeader();
  const rowHeight = 7;
  data.forEach((row, rowIndex) => {
    if (y + rowHeight > pageHeight - 15) {
      drawFooter();
      doc.addPage();
      pageNum++;
      y = margin;
      drawHeader();
      drawTableHeader();
    }
    if (rowIndex % 2 === 0) {
      doc.setFillColor(...colors.rowEven);
      doc.rect(margin, y, contentWidth, rowHeight, 'F');
    }
    doc.setDrawColor(...colors.lightGray);
    doc.setLineWidth(0.1);
    doc.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...colors.dark);
    let x = margin + 2;
    columns.forEach((col, i) => {
      const value = String(row[col.key] ?? '');
      const maxChars = Math.floor(finalWidths[i] / 1.8);
      const truncated = value.length > maxChars ? value.substring(0, maxChars - 2) + '..' : value;
      doc.text(truncated, x, y + 4.8);
      x += finalWidths[i];
    });
    y += rowHeight;
  });
  drawFooter();
  doc.save(filename);
};

// ==================== EXPORT FUNCTIONS ====================

export const exportPatientsToPdf = (patients: Patient[], getInsuranceName: (id: string | null) => string) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 200
  }, {
    header: 'Sexo',
    key: 'sexo',
    width: 80
  }, {
    header: 'Teléfono',
    key: 'telefono',
    width: 130
  }, {
    header: 'Email',
    key: 'email',
    width: 200
  }, {
    header: 'Dirección',
    key: 'direccion',
    width: 200
  }, {
    header: 'Seguro',
    key: 'seguro',
    width: 150
  }, {
    header: 'Registro',
    key: 'fecha',
    width: 100
  }];
  const data: PdfRow[] = patients.map(p => ({
    nombre: p.name,
    sexo: getGenderName(p.gender),
    telefono: p.phone,
    email: p.email,
    direccion: p.address,
    seguro: getInsuranceName(p.insuranceId),
    fecha: formatDate(p.createdAt)
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `pacientes_${ts}.pdf`, 'Listado de Pacientes');
};
export const exportDoctorsToPdf = (doctors: Doctor[], getSpecialtyName: (id: string) => string) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 200
  }, {
    header: 'Especialidad',
    key: 'especialidad',
    width: 200
  }, {
    header: 'Teléfono',
    key: 'telefono',
    width: 150
  }, {
    header: 'Email',
    key: 'email',
    width: 250
  }, {
    header: 'Registro',
    key: 'fecha',
    width: 120
  }];
  const data: PdfRow[] = doctors.map(d => ({
    nombre: d.name,
    especialidad: getSpecialtyName(d.specialtyId),
    telefono: d.phone,
    email: d.email,
    fecha: formatDate(d.createdAt)
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `doctores_${ts}.pdf`, 'Listado de Doctores');
};
export const exportAppointmentsToPdf = (appointments: Appointment[], getPatientName: (id: string) => string, getDoctorName: (id: string) => string, getServiceName: (id: string) => string) => {
  const columns: PdfColumn[] = [{
    header: 'Paciente',
    key: 'paciente',
    width: 170
  }, {
    header: 'Doctor',
    key: 'doctor',
    width: 170
  }, {
    header: 'Servicio',
    key: 'servicio',
    width: 150
  }, {
    header: 'Fecha',
    key: 'fecha',
    width: 90
  }, {
    header: 'Hora',
    key: 'hora',
    width: 60
  }, {
    header: 'Duración',
    key: 'duracion',
    width: 60
  }, {
    header: 'Estado',
    key: 'estado',
    width: 90
  }, {
    header: 'Creada por',
    key: 'creadaPor',
    width: 130
  }, {
    header: 'Notas',
    key: 'notas',
    width: 150
  }];
  const data: PdfRow[] = appointments.map(a => {
    const serviceIds = a.serviceIds && a.serviceIds.length > 0 ? a.serviceIds : [a.serviceId].filter(Boolean);
    const serviceNames = serviceIds.map(id => getServiceName(id)).join(', ');
    return {
      paciente: getPatientName(a.patientId),
      doctor: getDoctorName(a.doctorId),
      servicio: serviceNames,
      fecha: a.date,
      hora: a.time,
      duracion: `${a.duration} min`,
      estado: getStatusName(a.status),
      creadaPor: a.createdByUserName || '—',
      notas: a.notes || ''
    };
  });
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `citas_${ts}.pdf`, 'Listado de Citas');
};
export const exportServicesToPdf = (services: Service[]) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 250
  }, {
    header: 'Duración (min)',
    key: 'duracion',
    width: 100
  }, {
    header: 'Precio (DOP)',
    key: 'precio',
    width: 120
  }, {
    header: 'Descripción',
    key: 'descripcion',
    width: 400
  }];
  const data: PdfRow[] = services.map(s => ({
    nombre: s.name,
    duracion: s.duration,
    precio: s.price,
    descripcion: s.description || ''
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `servicios_${ts}.pdf`, 'Listado de Servicios');
};
export const exportInsurancesToPdf = (insurances: Insurance[]) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 200
  }, {
    header: 'Compañía',
    key: 'compania',
    width: 200
  }, {
    header: 'Cobertura',
    key: 'cobertura',
    width: 150
  }, {
    header: 'Teléfono',
    key: 'telefono',
    width: 150
  }, {
    header: 'Email',
    key: 'email',
    width: 250
  }];
  const data: PdfRow[] = insurances.map(i => ({
    nombre: i.name,
    compania: i.company,
    cobertura: i.coverageType,
    telefono: i.phone,
    email: i.email
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `seguros_${ts}.pdf`, 'Listado de Seguros');
};
export const exportSpecialtiesToPdf = (specialties: Specialty[]) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 250
  }, {
    header: 'Descripción',
    key: 'descripcion',
    width: 500
  }];
  const data: PdfRow[] = specialties.map(s => ({
    nombre: s.name,
    descripcion: s.description
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `especialidades_${ts}.pdf`, 'Listado de Especialidades');
};
export const exportContactsToPdf = (contacts: Contact[]) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 180
  }, {
    header: 'Empresa',
    key: 'empresa',
    width: 180
  }, {
    header: 'Cargo',
    key: 'cargo',
    width: 130
  }, {
    header: 'Teléfono',
    key: 'telefono',
    width: 130
  }, {
    header: 'Email',
    key: 'email',
    width: 200
  }, {
    header: 'Notas',
    key: 'notas',
    width: 200
  }];
  const data: PdfRow[] = contacts.map(c => ({
    nombre: c.name,
    empresa: c.company,
    cargo: c.position,
    telefono: c.phone,
    email: c.email,
    notas: c.notes || ''
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `contactos_${ts}.pdf`, 'Listado de Contactos');
};
export const exportCompaniesToPdf = (companies: any[]) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 180
  }, {
    header: 'Industria',
    key: 'industria',
    width: 130
  }, {
    header: 'Tamaño',
    key: 'tamano',
    width: 100
  }, {
    header: 'Ubicación',
    key: 'ubicacion',
    width: 130
  }, {
    header: 'Teléfono',
    key: 'telefono',
    width: 130
  }, {
    header: 'Email',
    key: 'email',
    width: 200
  }, {
    header: 'Sitio Web',
    key: 'sitioWeb',
    width: 180
  }];
  const data: PdfRow[] = companies.map(c => ({
    nombre: c.name,
    industria: c.industry,
    tamano: c.size,
    ubicacion: c.location,
    telefono: c.phone,
    email: c.email,
    sitioWeb: c.website || ''
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `empresas_${ts}.pdf`, 'Listado de Empresas');
};
export const exportOpportunitiesToPdf = (opportunities: Opportunity[]) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 180
  }, {
    header: 'Empresa',
    key: 'empresa',
    width: 150
  }, {
    header: 'Etapa',
    key: 'etapa',
    width: 130
  }, {
    header: 'Monto (DOP)',
    key: 'monto',
    width: 100
  }, {
    header: 'Probabilidad',
    key: 'probabilidad',
    width: 80
  }, {
    header: 'Notas',
    key: 'notas',
    width: 250
  }, {
    header: 'Fecha',
    key: 'fecha',
    width: 100
  }];
  const data: PdfRow[] = opportunities.map(o => ({
    nombre: o.name,
    empresa: o.company,
    etapa: o.stage,
    monto: o.amount,
    probabilidad: `${o.probability}%`,
    notas: o.notes || '',
    fecha: formatDate(o.createdAt)
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `oportunidades_${ts}.pdf`, 'Listado de Oportunidades');
};
export const exportClinicalHistoriesToPdf = (histories: ClinicalHistory[], getPatientName: (id: string) => string) => {
  const columns: PdfColumn[] = [{
    header: 'Paciente',
    key: 'paciente',
    width: 180
  }, {
    header: 'Doctor',
    key: 'doctor',
    width: 180
  }, {
    header: 'Fecha',
    key: 'fecha',
    width: 100
  }, {
    header: 'Motivo',
    key: 'motivo',
    width: 250
  }, {
    header: 'Diagnóstico',
    key: 'diagnostico',
    width: 250
  }, {
    header: 'Tratamiento',
    key: 'tratamiento',
    width: 250
  }];
  const data: PdfRow[] = histories.map(h => ({
    paciente: getPatientName(h.patientId),
    doctor: h.doctorName,
    fecha: formatDate(h.date),
    motivo: h.personalBackground.consultationReason || '',
    diagnostico: h.diagnosis || '',
    tratamiento: h.treatment || ''
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `historiales_clinicos_${ts}.pdf`, 'Historiales Clínicos');
};
export const exportFollowUpNotesToPdf = (notes: any[]) => {
  const cleanText = (text: any): string => {
    if (!text) return '';
    let cleaned = String(text);
    cleaned = cleaned.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
    cleaned = cleaned.replace(/[\u2018\u2019]/g, "'");
    cleaned = cleaned.replace(/[\u201C\u201D]/g, '"');
    cleaned = cleaned.replace(/\u2013/g, '-');
    cleaned = cleaned.replace(/\u2014/g, '--');
    cleaned = cleaned.replace(/\u2026/g, '...');
    cleaned = cleaned.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '');
    cleaned = cleaned.replace(/[\u2600-\u27BF]/g, '');
    return cleaned.trim();
  };
  const columns: PdfColumn[] = [{
    header: 'Fecha',
    key: 'fecha',
    width: 120
  }, {
    header: 'Usuario',
    key: 'usuario',
    width: 180
  }, {
    header: 'Contacto',
    key: 'contacto',
    width: 180
  }, {
    header: 'Asunto',
    key: 'asunto',
    width: 220
  }, {
    header: 'Descripción',
    key: 'descripcion',
    width: 350
  }];
  const data: PdfRow[] = notes.map(n => ({
    fecha: formatDate(n.createdAt),
    usuario: cleanText(n.userName),
    contacto: cleanText(n.contactName),
    asunto: cleanText(n.subject),
    descripcion: cleanText(n.description)
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `notas_seguimiento_${ts}.pdf`, 'Notas de Seguimiento');
};
export const exportAuditLogsToPdf = (auditLogs: AuditLog[]) => {
  const getActionName = (action: string): string => {
    switch (action) {
      case 'create':
        return 'Creación';
      case 'update':
        return 'Actualización';
      case 'delete':
        return 'Eliminación';
      case 'complete':
        return 'Completado';
      case 'cancel':
        return 'Cancelación';
      case 'reschedule':
        return 'Reprogramación';
      default:
        return action;
    }
  };
  const columns: PdfColumn[] = [{
    header: 'Fecha y Hora',
    key: 'fechaHora',
    width: 150
  }, {
    header: 'Usuario',
    key: 'usuario',
    width: 160
  }, {
    header: 'Acción',
    key: 'accion',
    width: 100
  }, {
    header: 'Módulo',
    key: 'modulo',
    width: 100
  }, {
    header: 'Entidad',
    key: 'entidad',
    width: 160
  }, {
    header: 'Detalles',
    key: 'detalles',
    width: 300
  }];
  const data: PdfRow[] = auditLogs.map(log => ({
    fechaHora: new Date(log.timestamp).toLocaleString('es-DO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Santo_Domingo'
    }),
    usuario: log.userName,
    accion: getActionName(log.action),
    modulo: log.module.charAt(0).toUpperCase() + log.module.slice(1),
    entidad: log.entityName,
    detalles: log.details
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `bitacora_${ts}.pdf`, 'Bitácora de Auditoría');
};

// ==================== DASHBOARD & STATS EXPORT FUNCTIONS ====================

export const exportDashboardChartDataToPdf = (chartData: {
  label: string;
  values: Record<string, string | number>;
}[], columns: {
  header: string;
  key: string;
}[], title: string, dateRange?: {
  from: string;
  to: string;
}) => {
  const pdfColumns: PdfColumn[] = columns.map(c => ({
    header: c.header,
    key: c.key,
    width: Math.max(100, Math.floor(900 / columns.length))
  }));
  const data: PdfRow[] = chartData.map(item => ({
    ...item.values
  }));
  const dateInfo = dateRange ? ` (${dateRange.from} al ${dateRange.to})` : '';
  const ts = new Date().toISOString().split('T')[0];
  const filename = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
  downloadFormattedPdf(data, pdfColumns, `${filename}_${ts}.pdf`, `${title}${dateInfo}`);
};
export const exportServiceDetailToPdf = (serviceData: {
  name: string;
  count: number;
  totalRevenue: number;
  avgPerAppointment: number;
  percentOfTotal: number;
}[], grandTotalRevenue: number, grandTotalCount: number, dateRange?: {
  from: string;
  to: string;
}) => {
  const columns: PdfColumn[] = [{
    header: 'Servicio',
    key: 'servicio',
    width: 250
  }, {
    header: 'Cantidad',
    key: 'cantidad',
    width: 100
  }, {
    header: 'Ingresos Totales',
    key: 'ingresos',
    width: 150
  }, {
    header: 'Promedio / Cita',
    key: 'promedio',
    width: 150
  }, {
    header: '% del Total',
    key: 'porcentaje',
    width: 100
  }];
  const formatCurrencyLocal = (n: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(n);
  const data: PdfRow[] = [...serviceData.map(s => ({
    servicio: s.name,
    cantidad: s.count,
    ingresos: formatCurrencyLocal(s.totalRevenue),
    promedio: s.count > 0 ? formatCurrencyLocal(s.avgPerAppointment) : '-',
    porcentaje: `${s.percentOfTotal.toFixed(1)}%`
  })), {
    servicio: 'TOTAL',
    cantidad: grandTotalCount,
    ingresos: formatCurrencyLocal(grandTotalRevenue),
    promedio: grandTotalCount > 0 ? formatCurrencyLocal(grandTotalRevenue / grandTotalCount) : '-',
    porcentaje: '100%'
  }];
  const dateInfo = dateRange ? ` (${dateRange.from} al ${dateRange.to})` : '';
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `detalle_servicios_${ts}.pdf`, `Detalle por Servicio${dateInfo}`);
};
export const exportRevenueRankingToPdf = (rankingData: {
  name: string;
  amount: number;
  count?: number;
}[], title: string, dateRange?: {
  from: string;
  to: string;
}) => {
  const formatCurrencyLocal = (n: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(n);
  const hasCount = rankingData.some(d => d.count !== undefined);
  const columns: PdfColumn[] = [{
    header: '#',
    key: 'posicion',
    width: 50
  }, {
    header: 'Nombre',
    key: 'nombre',
    width: 300
  }, ...(hasCount ? [{
    header: 'Citas',
    key: 'citas',
    width: 100
  } as PdfColumn] : []), {
    header: 'Ingresos',
    key: 'ingresos',
    width: 200
  }];
  const totalAmount = rankingData.reduce((s, d) => s + d.amount, 0);
  const data: PdfRow[] = [...rankingData.map((d, i) => ({
    posicion: i + 1,
    nombre: d.name,
    ...(hasCount ? {
      citas: d.count || 0
    } : {}),
    ingresos: formatCurrencyLocal(d.amount)
  })), {
    posicion: '',
    nombre: 'TOTAL',
    ...(hasCount ? {
      citas: rankingData.reduce((s, d) => s + (d.count || 0), 0)
    } : {}),
    ingresos: formatCurrencyLocal(totalAmount)
  }];
  const dateInfo = dateRange ? ` (${dateRange.from} al ${dateRange.to})` : '';
  const ts = new Date().toISOString().split('T')[0];
  const filename = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
  downloadFormattedPdf(data, columns, `${filename}_${ts}.pdf`, `${title}${dateInfo}`);
};
export const exportProductsToPdf = (products: {
  id: string;
  name: string;
  sku: string;
  category: string;
  price: number;
  cost: number;
  stock: number;
  status: string;
  description?: string;
}[]) => {
  const columns: PdfColumn[] = [{
    header: 'Nombre',
    key: 'nombre',
    width: 180
  }, {
    header: 'SKU',
    key: 'sku',
    width: 100
  }, {
    header: 'Categoría',
    key: 'categoria',
    width: 120
  }, {
    header: 'Precio',
    key: 'precio',
    width: 100
  }, {
    header: 'Costo',
    key: 'costo',
    width: 100
  }, {
    header: 'Stock',
    key: 'stock',
    width: 70
  }, {
    header: 'Estado',
    key: 'estado',
    width: 90
  }];
  const data: PdfRow[] = products.map(p => ({
    nombre: p.name,
    sku: p.sku || '',
    categoria: p.category || '',
    precio: `RD$ ${p.price.toLocaleString('es-DO')}`,
    costo: `RD$ ${p.cost.toLocaleString('es-DO')}`,
    stock: p.stock,
    estado: p.status === 'active' ? 'Activo' : p.status === 'inactive' ? 'Inactivo' : p.status === 'archived' ? 'Archivado' : p.status
  }));
  const ts = new Date().toISOString().split('T')[0];
  downloadFormattedPdf(data, columns, `productos_${ts}.pdf`, 'Listado de Productos');
};
export const exportProductSalesToPdf = async (sales: any[], getPatientName: (id: string) => string, dateFrom?: string | null, dateTo?: string | null) => {
  const columns: PdfColumn[] = [{
    header: 'Fecha',
    key: 'fecha',
    width: 80
  }, {
    header: 'Cliente',
    key: 'cliente',
    width: 140
  }, {
    header: 'Productos',
    key: 'productos',
    width: 220
  }, {
    header: 'Total',
    key: 'total',
    width: 90
  }, {
    header: 'Descuento',
    key: 'descuento',
    width: 90
  }];
  let data2export = sales;
  if (dateFrom && dateTo) {
    data2export = sales.filter((s: any) => {
      const d = (s.date || '').toString().split('T')[0];
      return d >= dateFrom && d <= dateTo;
    });
  }
  const data: PdfRow[] = data2export.map((s: any) => ({
    fecha: (s.date || '').toString().split('T')[0],
    cliente: getPatientName(s.patientId),
    productos: Array.isArray(s.items) ? s.items.map((it: any) => `${it.quantity || 1}x ${it.productName || it.productId || ''}`).join(', ') : '',
    total: `RD$ ${Number(s.total || 0).toLocaleString('es-DO')}`,
    descuento: `RD$ ${Number(s.discountTotal || s.discount_total || 0).toLocaleString('es-DO')}`
  }));
  const ts = new Date().toISOString().split('T')[0];
  await downloadFormattedPdf(data, columns, `ventas_productos_${ts}.pdf`, 'Ventas de Productos');
};
export const exportProductStatsToPdf = async (productStats: any[], revenueTrend: any[], totals: {
  revenue: number;
  units: number;
  profit: number;
}, dateFrom?: string | null, dateTo?: string | null) => {
  const columns: PdfColumn[] = [{
    header: 'Producto',
    key: 'producto',
    width: 200
  }, {
    header: 'Unidades',
    key: 'unidades',
    width: 90
  }, {
    header: 'Ingresos',
    key: 'ingresos',
    width: 110
  }, {
    header: 'Ganancia',
    key: 'ganancia',
    width: 110
  }];
  const data: PdfRow[] = (productStats || []).map((p: any) => ({
    producto: p.name || p.productName || '',
    unidades: Number(p.units || p.quantity || 0),
    ingresos: `RD$ ${Number(p.revenue || 0).toLocaleString('es-DO')}`,
    ganancia: `RD$ ${Number((p.revenue || 0) - (p.cost || 0)).toLocaleString('es-DO')}`
  }));
  data.push({
    producto: '── TOTAL ──',
    unidades: totals.units,
    ingresos: `RD$ ${totals.revenue.toLocaleString('es-DO')}`,
    ganancia: `RD$ ${totals.profit.toLocaleString('es-DO')}`
  });
  const ts = new Date().toISOString().split('T')[0];
  const suffix = dateFrom && dateTo ? `_${dateFrom}_${dateTo}` : '';
  await downloadFormattedPdf(data, columns, `estadisticas_productos${suffix}_${ts}.pdf`, 'Estadísticas de Productos');
};